import { useCallback, useEffect, useRef, useState } from 'react';
import { Pressable, View } from 'react-native';

import { Menu, MenuItem, Text } from '@/components/ui';
import { fetchChatModels } from '@/lib/chat/api';
import type { LlmModel } from '@/lib/chat/types';

type ChatModelPickerProps = {
  value: string;
  disabled?: boolean;
  onChange: (modelId: string) => void;
};

export function ChatModelPicker({ value, disabled, onChange }: ChatModelPickerProps) {
  const [models, setModels] = useState<LlmModel[]>([]);
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<{ left: number; top: number } | null>(null);
  const triggerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    void fetchChatModels()
      .then(setModels)
      .catch(() => setModels([]));
  }, []);

  const selected = models.find((model) => model.id === value);

  const openMenu = useCallback(() => {
    if (disabled) {
      return;
    }
    const target = triggerRef.current;
    if (target && typeof target.getBoundingClientRect === 'function') {
      const rect = target.getBoundingClientRect();
      setAnchor({ left: rect.left, top: rect.bottom + 4 });
    } else {
      setAnchor({ left: 16, top: 64 });
    }
    setOpen(true);
  }, [disabled]);

  return (
    <>
      <Pressable
        ref={triggerRef as never}
        className="rounded border border-border px-2 py-1"
        disabled={disabled}
        onPress={openMenu}
      >
        <Text variant="body-sm">{selected?.displayName ?? value}</Text>
      </Pressable>
      <Menu anchor={anchor} open={open} onClose={() => setOpen(false)}>
        <View className="max-h-64 overflow-y-auto">
          {models.map((model) => (
            <MenuItem
              key={model.id}
              label={model.displayName}
              onPress={() => {
                onChange(model.id);
                setOpen(false);
              }}
            />
          ))}
        </View>
      </Menu>
    </>
  );
}
